import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const TEAL = "#0E8C8C";
const DEEP = "#0A3A3E";
const NAME = "21 Dental Clinic";
const FB = "https://www.facebook.com/p/21-Dental-Clinic-100064084718508/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function TwentyOneConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Services", "#services"], ["Inside", "#inside"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#132B2C", fontFamily: "'Nunito Sans', sans-serif" }} data-testid="21dental-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Nunito+Sans:wght@400;500;600;700&display=swap'); .td21-heading{font-family:'Poppins',sans-serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="td21-logo" className="td21-heading text-xl font-semibold" style={{ color: DEEP }}>21 Dental Clinic</a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#3A5254] hover:text-[#0E8C8C] transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="td21-header-cta" className="hidden sm:inline-flex rounded-full px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: TEAL }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="td21-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="td21-hero">
        <img src="/dental/21-dental-clinic-concept/hero.jpg" alt="21 Dental Clinic storefront, Twin Arcade Building, Dumaguete City" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "62% 20%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,58,62,.9) 0%, rgba(10,58,62,.72) 52%, rgba(10,58,62,.12) 86%), linear-gradient(0deg, rgba(10,58,62,.55), transparent 58%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24">
          <motion.div {...fadeUp} className="max-w-xl bg-[#0A3A3E]/35 p-5 sm:p-7 backdrop-blur-[1px]">
            <span className="rounded-full px-4 py-1.5 text-xs font-semibold" style={{ background: TEAL, color: "white" }}>Twin Arcade Building, Dumaguete City</span>
            <h1 className="mt-5 text-4xl sm:text-5xl td21-heading font-bold text-white leading-[1.1]">General Dentistry, Cosmetic Dentistry &amp; Orthodontics</h1>
            <p className="mt-5 text-lg text-white/80">Care led by Dr. Jonathan W. Cunanan on the 2nd floor of Twin Arcade, Perdices St.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#visit" data-testid="td21-hero-book" className="rounded-full px-8 py-3.5 text-sm font-semibold text-white" style={{ background: TEAL }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="td21-hero-message" className="rounded-full px-8 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
            <p className="mt-7 text-sm text-white/60">100% Facebook Recommend · 5 Reviews</p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl td21-heading font-semibold leading-relaxed" style={{ color: DEEP }}>
          21 Dental Clinic offers general dentistry, cosmetic dentistry and orthodontics from a Perdices Street clinic in Dumaguete City.
        </motion.p>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20" style={{ background: "#EAF6F5" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="td21-heading text-3xl font-semibold text-center mb-12" style={{ color: DEEP }}>Our Services</motion.h2>
          <div className="grid sm:grid-cols-4 gap-6 text-center">
            {["General Dentistry", "Cosmetic Dentistry", "Orthodontics", "Tooth Extraction"].map((s) => (
              <motion.div key={s} {...fadeUp} className="py-6 bg-white rounded-xl shadow-sm">
                <p className="font-semibold text-sm" style={{ color: DEEP }}>{s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INSIDE — real practice photos */}
      <section id="inside" className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="td21-heading text-3xl font-semibold text-center mb-12" style={{ color: DEEP }}>Inside the Clinic</motion.h2>
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <motion.div {...fadeUp} className="overflow-hidden rounded-2xl shadow-md">
            <img src="/dental/21-dental-clinic-concept/interior.jpg" alt="Treatment room at 21 Dental Clinic" className="w-full h-72 object-cover" />
            <p className="p-4 bg-white text-sm font-medium" style={{ color: DEEP }}>The treatment room</p>
          </motion.div>
          <motion.div {...fadeUp} className="overflow-hidden rounded-2xl shadow-md">
            <img src="/dental/21-dental-clinic-concept/treatment.jpg" alt="A real treatment in progress at 21 Dental Clinic" className="w-full h-72 object-cover" />
            <p className="p-4 bg-white text-sm font-medium" style={{ color: DEEP }}>Care in progress</p>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="overflow-hidden rounded-2xl shadow-md max-w-xl mx-auto">
          <img src="/dental/21-dental-clinic-concept/lab-molds.jpg" alt="Dental impressions prepared in-house at 21 Dental Clinic" className="w-full h-56 object-cover" />
          <p className="p-4 bg-white text-sm font-medium" style={{ color: DEEP }}>Impressions, prepared in-house</p>
        </motion.div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-20" style={{ background: DEEP }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="td21-heading text-3xl font-semibold text-white">Visit Us</h2>
            <ul className="mt-8 space-y-5 text-white/75">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: TEAL }} className="mt-0.5" />Room 21, 2/F Twin Arcade Building, Perdices St., Dumaguete City</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: TEAL }} className="mt-0.5" /><a href="tel:+639269496058" className="hover:underline">0926 949 6058</a> · (035) 421-0046</li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: TEAL }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="td21-facebook-link" className="hover:underline">21 Dental Clinic</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px] rounded-2xl overflow-hidden shadow-md">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Twin Arcade Building Perdices St Dumaguete City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#5A7274]">
          <p className="td21-heading text-base font-semibold" style={{ color: DEEP }}>21 Dental Clinic</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: TEAL }}>Message 21 Dental</a>
      <ConceptCTAPill slug="21-dental-clinic-concept" />
    </div>
  );
}
